import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { CanvaExportOptions } from '@/types/canva';
import { SelectedDesign } from './CanvaImportDialog';

/**
 * CanvaExportConfig Component
 *
 * Step 3: Configure export options per selected design
 */

interface CanvaExportConfigProps {
  selectedDesigns: SelectedDesign[];
  onUpdateOptions: (designId: string, options: Partial<CanvaExportOptions>) => void;
}

export const CanvaExportConfig = ({
  selectedDesigns,
  onUpdateOptions,
}: CanvaExportConfigProps) => {
  const { t } = useTranslation('common');
  const [globalFormat, setGlobalFormat] = useState<string>('');
  const [globalQuality, setGlobalQuality] = useState<string>('');

  /**
   * Apply options to all designs
   */
  const handleApplyToAll = () => {
    if (!globalFormat && !globalQuality) return;

    selectedDesigns.forEach((selected) => {
      const updates: Partial<CanvaExportOptions> = {};

      if (globalFormat) {
        updates.format = globalFormat as CanvaExportOptions['format'];
      }

      if (globalQuality) {
        updates.quality = globalQuality as CanvaExportOptions['quality'];
      }

      onUpdateOptions(selected.design.id, updates);
    });

    // Reset global selects
    setGlobalFormat('');
    setGlobalQuality('');
  };

  /**
   * Get available format options for a design
   * All formats are always available - Canva API supports exporting any design as video/gif
   */
  const getFormatOptions = () => [
    { value: 'png', label: 'PNG' },
    { value: 'jpg', label: 'JPG' },
    { value: 'pdf', label: 'PDF' },
    { value: 'gif', label: 'GIF' },
    { value: 'mp4', label: 'MP4 Video' },
  ];

  const qualityOptions = [
    { value: 'full', label: t('canva_quality_full', { defaultValue: 'Full Quality' }) },
    { value: 'medium', label: t('canva_quality_medium', { defaultValue: 'Social Media' }) },
    { value: 'low', label: t('canva_quality_low', { defaultValue: 'Draft' }) },
  ];

  return (
    <div className="space-y-6">
      {/* Apply to all section */}
      <div className="bg-soft-mint border border-light-teal-gray rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon
            name={ICONS.INFO}
            size="sm"
            color="text-dark-teal"
            ariaHidden
          />
          <h3 className="text-sm font-medium text-dark-teal">
            {t('apply_to_all_designs')}
          </h3>
        </div>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <Select
              placeholder={t('export_format')}
              options={getFormatOptions()}
              value={globalFormat}
              onValueChange={setGlobalFormat}
              size="sm"
            />
          </div>
          <div className="flex-1">
            <Select
              placeholder={t('export_quality')}
              options={qualityOptions}
              value={globalQuality}
              onValueChange={setGlobalQuality}
              size="sm"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleApplyToAll}
            disabled={!globalFormat && !globalQuality}
            className="text-xs whitespace-nowrap h-10"
          >
            {t('apply_to_all')}
          </Button>
        </div>
      </div>

      {/* Per-design options */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {selectedDesigns.map((selected) => (
          <div
            key={selected.design.id}
            className="border border-gray-200 rounded-lg p-4 space-y-3"
          >
            {/* Design header */}
            <div className="flex gap-3">
              <img
                src={selected.design.thumbnailUrl}
                alt={selected.design.title}
                className="w-16 h-16 rounded object-cover flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 truncate">
                  {selected.design.title}
                </h4>
                <div className="flex gap-2 mt-1">
                  {selected.design.isMultiPage && (
                    <span className="text-xs text-gray-500">
                      {selected.design.pageCount} {t('pages')}
                    </span>
                  )}
                  {selected.design.hasVideo && (
                    <span className="text-xs text-gray-500">{t('video')}</span>
                  )}
                  {selected.design.hasAnimation && (
                    <span className="text-xs text-gray-500">{t('animated')}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Format */}
              <Select
                label={t('export_format')}
                options={getFormatOptions()}
                value={selected.exportOptions.format}
                onValueChange={(value) =>
                  onUpdateOptions(selected.design.id, {
                    format: value as CanvaExportOptions['format'],
                  })
                }
              />

              {/* Quality (applies to all formats) */}
              <Select
                label={t('export_quality')}
                options={qualityOptions}
                value={selected.exportOptions.quality}
                onValueChange={(value) =>
                  onUpdateOptions(selected.design.id, {
                    quality: value as CanvaExportOptions['quality'],
                  })
                }
              />
            </div>

            {/* Merge as carousel (only for multi-page image exports, not PDF/GIF/MP4) */}
            {selected.design.isMultiPage &&
              !['pdf', 'gif', 'mp4'].includes(selected.exportOptions.format) && (
              <Checkbox
                id={`carousel-${selected.design.id}`}
                label={t('merge_as_carousel')}
                checked={selected.exportOptions.mergeAsCarousel}
                onCheckedChange={(checked) =>
                  onUpdateOptions(selected.design.id, {
                    mergeAsCarousel: checked,
                  })
                }
              />
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          <strong>{t('ready_to_import')}:</strong>{' '}
          {t('canva_designs_selected', { count: selectedDesigns.length })}
        </p>
      </div>
    </div>
  );
};
